import { useState, useEffect, useRef } from "react";
import { Info, Check, X, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

/**
 * InfoHint (Feb-2026, User-Wunsch) — ersetzt Erklärtexte in der Disposition durch
 * ein „i" im Kreis. Verhalten:
 *  - Hover / Tap zeigt den Erklärtext als kleines Popover.
 *  - Administratoren können per Doppelklick den Text bearbeiten (wird in der DB
 *    gespeichert und gilt danach für alle Nutzer).
 *  - Defaults stehen direkt im Code (defaultText); das Backend speichert nur
 *    Overrides (/api/dispo/help-texts).
 */

// --- gemeinsamer Cache für die Override-Texte (einmal laden) -----------------
let _cache = null;
let _inflight = null;
const _listeners = new Set();

function loadHelpTexts() {
    if (_cache) return Promise.resolve(_cache);
    if (!_inflight) {
        _inflight = api.get("/dispo/help-texts")
            .then((r) => { _cache = r.data?.overrides || {}; _inflight = null; _emit(); return _cache; })
            .catch(() => { _cache = {}; _inflight = null; return _cache; });
    }
    return _inflight;
}
function _emit() { _listeners.forEach((fn) => fn(_cache)); }
function setOverride(key, text) { _cache = { ...(_cache || {}), [key]: text }; _emit(); }

// Beim Zurückkehren auf den Tab die Erklärtexte neu laden (falls ein Admin sie
// zwischenzeitlich in einem anderen Tab/Account geändert hat).
if (typeof window !== "undefined" && !window.__twHelpFocusBound) {
    window.__twHelpFocusBound = true;
    window.addEventListener("focus", () => { _cache = null; _inflight = null; loadHelpTexts(); });
}

export function InfoHint({ id, defaultText, side = "top", className = "" }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [overrides, setOverrides] = useState(_cache || {});
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        loadHelpTexts().then(setOverrides);
        const fn = (c) => setOverrides({ ...c });
        _listeners.add(fn);
        return () => { _listeners.delete(fn); };
    }, []);

    // Klick außerhalb schließt Popover/Editor
    useEffect(() => {
        if (!open && !editing) return;
        const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setEditing(false); } };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open, editing]);

    const text = overrides[id] ?? defaultText;

    const startEdit = () => {
        if (!isAdmin) return;
        setDraft(text || "");
        setEditing(true);
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/dispo/help-texts/${encodeURIComponent(id)}`, { text: draft });
            setOverride(id, data?.text ?? draft);
            setEditing(false);
        } catch { /* still close on failure */ }
        finally { setSaving(false); }
    };

    // Jul-2026 (User-Wunsch): Erklärtexte öffnen sich immer nach OBEN, damit der
    // Mauszeiger den Text nicht verdeckt (früheres side="bottom" wird ignoriert).
    const sideCls = side === "right" ? "left-full ml-1.5 top-0" : "bottom-full mb-1.5";

    return (
        <span ref={wrapRef} className={`relative inline-flex ${className}`}>
            <button
                type="button"
                onMouseEnter={() => !editing && setOpen(true)}
                onMouseLeave={() => !editing && setOpen(false)}
                onClick={(e) => { e.preventDefault(); setOpen((o) => !o || editing); }}
                onDoubleClick={(e) => { e.preventDefault(); startEdit(); }}
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 hover:bg-[#111827] hover:text-white transition-colors align-middle"
                aria-label="Erklärung anzeigen"
                data-testid={`info-hint-${id}`}
            >
                <Info className="w-2.5 h-2.5" />
            </button>

            {(open || editing) && (
                <span
                    className={`absolute z-50 ${sideCls} left-0 w-56 max-w-[15rem] bg-[#111827] text-white text-[11px] leading-snug rounded-sm p-2.5 shadow-lg normal-case font-normal tracking-normal`}
                    data-testid={`info-hint-popover-${id}`}
                    onMouseEnter={() => !editing && setOpen(true)}
                    onMouseLeave={() => !editing && setOpen(false)}
                >
                    {editing ? (
                        <span className="block">
                            <textarea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                rows={4}
                                className="w-full text-[11px] text-gray-900 rounded-sm p-1.5 resize-none focus:outline-none"
                                data-testid={`info-hint-editor-${id}`}
                                autoFocus
                            />
                            <span className="flex items-center justify-end gap-1.5 mt-1.5">
                                <button type="button" onClick={() => setEditing(false)} className="inline-flex items-center gap-1 px-2 h-6 rounded-sm bg-gray-600 hover:bg-gray-500 text-[10px] font-bold" data-testid={`info-hint-cancel-${id}`}>
                                    <X className="w-3 h-3" /> Abbr.
                                </button>
                                <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-2 h-6 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-[10px] font-bold disabled:opacity-60" data-testid={`info-hint-save-${id}`}>
                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Speichern
                                </button>
                            </span>
                        </span>
                    ) : (
                        <span className="block">
                            {text || <em className="text-gray-400">Keine Erklärung hinterlegt.</em>}
                            {isAdmin && <span className="block mt-1 text-[9px] text-gray-400">Doppelklick auf „i" zum Bearbeiten</span>}
                        </span>
                    )}
                </span>
            )}
        </span>
    );
}

export default InfoHint;
